import styled from "styled-components"
import { tContext } from "../types"
import { BasicTkTimeType } from "../../../common/types"
import GroupRowFormattingDefaultCellRenderer from "./GroupRowFormattingDefaultCellRenderer"
import LaborRateMatchBadge from "./LaborRateMatchBadge"
import { spacingSsm } from "@rhumbix/rmbx_design_system_web"
import { selectCompanyFeatureToggles } from "../../../selectors/user"
import { useSelector } from "react-redux"
import { iCellRendererParams } from "./types"

/**
 * Props for the TKEntryFieldCellRenderer component.
 * Extends AG Grid's ICellRendererParams with additional context and timeType configuration.
 */
interface Props extends iCellRendererParams {
    /** The table context containing entities and referenceable data */
    context: tContext
    /** The time type to check for rate matches: ST, OT, or DT */
    timeType: BasicTkTimeType
}

/**
 * Cell renderer for timecard entry fields that displays time values with badges.
 * Renders the default cell content and badges for displaying additional information.
 *
 * Usage:
 * Used in AG Grid column definitions for ST, OT, DT columns in the weekly timecard modal table
 * i.e. settings-files/timekeeping-entries.jsx
 */
const TKEntryFieldCellRenderer: React.FC<Props> = ({ timeType, ...props }) => {
    const { node, colDef } = props
    const featureToggles = useSelector(selectCompanyFeatureToggles)
    const isHistory = Boolean(colDef?.history)

    const WrappedCellRenderer = <GroupRowFormattingDefaultCellRenderer includeCico={false} {...props} />
    const badges = [
        featureToggles.rate_tables ? (
            <LaborRateMatchBadge
                key="labor-rate-match"
                node={node}
                timeType={timeType}
                skipClientsideMatch={isHistory}
            />
        ) : null,
    ].filter(badge => badge != null)

    return (
        <>
            {WrappedCellRenderer}
            {badges.length ? <BadgeContainer>{badges}</BadgeContainer> : null}
        </>
    )
}

const BadgeContainer = styled.div`
    position: absolute;
    right: ${spacingSsm};
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
`

export default TKEntryFieldCellRenderer
